import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {motion, useInView} from 'framer-motion'
import type {ProductDeepDiveV2SliceDefaultPrimaryProductsItem} from 'prismicio-types'
import {useRef} from 'react'

const itemVariants = {
  hidden: {opacity: 0},
  visible: (custom: number) => ({
    opacity: 1,
    transition: {delay: custom * 0.1, duration: 0.6, ease: 'easeIn'},
  }),
}

type ProductCardProps = {
  product: ProductDeepDiveV2SliceDefaultPrimaryProductsItem
  index: number
}

export const ProductCard = ({product, index}: ProductCardProps) => {
  const ref = useRef(null)
  const isInView = useInView(ref, {amount: 0.1, once: false})

  return (
    <motion.div
      key={index}
      ref={ref}
      custom={index}
      variants={itemVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      className={cn(
        'pg:pt-6 flex flex-col justify-between rounded-lg border border-moss-white/[10%] bg-[#002916] px-4 pt-4 max-lg:gap-6 lg:px-6',
        {
          'lg:min-h-[486px] col-span-full md:col-span-6 gap-12 pb-0': index < 2,
          'lg:min-h-[436px] col-span-full md:col-span-4 pb-4 lg:pb-6': index >= 2,
        },
      )}
    >
      <div className="flex flex-col gap-2 lg:gap-3">
        <p className="text-tg-body-l text-moss-white">{product.title}</p>
        <div className="flex flex-col justify-between gap-6">
          {isFilled.richText(product.description_rte) ? (
            <div>
              <PrismicRichText
                field={product.description_rte}
                components={{
                  paragraph: ({children}) => <p className="text-tg-body-m text-moss-white/[70%]">{children}</p>,
                }}
              />
            </div>
          ) : (
            <p className={cn('text-tg-body-m   text-moss-white/[70%]', {'max-w-[294px]': index < 2})}>
              {product.description}
            </p>
          )}
        </div>
      </div>

      <picture>
        {isFilled.imageThumbnail(product.asset.Mobile) && (
          <source media="(max-width: 768px)" srcSet={product.asset.Mobile.url} sizes="50vw" />
        )}
        <PrismicNextImage quality={100} field={product.asset} className="object-cover max-lg:min-h-[214px]" />
      </picture>
    </motion.div>
  )
}
